<template>
  <div>
    <div class="adm-toolbar">
      <div class="adm-search">
        <i class="fas fa-search"></i>
        <input v-model.trim="search" class="adm-input" placeholder="Search name, ID or city" aria-label="Search clinics" />
      </div>
      <div class="adm-toolbar__spacer"></div>
      <button type="button" class="adm-btn adm-btn--primary" @click="openCreate">
        <i class="fas fa-plus"></i> Add clinic
      </button>
    </div>

    <div class="adm-card">
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead>
            <tr>
              <th>Clinic</th>
              <th>City</th>
              <th>Contact</th>
              <th>Doctors</th>
              <th>Staff logins</th>
              <th class="adm-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr v-for="n in 4" :key="`s${n}`">
                <td v-for="c in 5" :key="c"><div class="adm-skeleton" style="width: 70%"></div></td>
                <td></td>
              </tr>
            </template>
            <tr v-else-if="!filtered.length">
              <td colspan="6">
                <div class="adm-empty">
                  <i class="fas fa-hospital"></i>
                  <strong>{{ search ? "No clinic matches your search" : "No clinics yet" }}</strong>
                  <template v-if="!search">Add the first clinic to start assigning doctors.</template>
                </div>
              </td>
            </tr>
            <template v-else>
              <tr v-for="clinic in filtered" :key="clinic.clinicid">
                <td>
                  <div class="adm-person">
                    <span class="adm-avatar adm-tone-teal"><i class="fas fa-hospital"></i></span>
                    <span>
                      <span class="adm-person__name">{{ clinic.clinicname }}</span>
                      <span class="adm-person__meta adm-mono">{{ clinic.clinicid }}</span>
                    </span>
                  </div>
                </td>
                <td>
                  <div>{{ clinic.locationname || "—" }}</div>
                  <div class="adm-muted adm-clinic-address">{{ clinic.clinicaddress || "" }}</div>
                </td>
                <td>
                  <div>{{ clinic.contactnumbers || "—" }}</div>
                  <div class="adm-muted">{{ isPlaceholderEmail(clinic.email) ? "" : clinic.email }}</div>
                </td>
                <td><span class="adm-badge adm-badge--plain">{{ clinic.doctor_count }}</span></td>
                <td><span class="adm-badge adm-badge--plain">{{ clinic.user_count }}</span></td>
                <td class="adm-col-actions">
                  <button type="button" class="adm-btn adm-btn--sm" @click="openDoctors(clinic)">
                    <i class="fas fa-user-md"></i> Doctors
                  </button>
                  <button type="button" class="adm-btn adm-btn--sm" :disabled="!clinic.doctor_count"
                          :title="clinic.doctor_count ? '' : 'Assign a doctor to this clinic first'"
                          @click="$emit('navigate', 'clinic-users', { create: clinic.clinicid })">
                    <i class="fas fa-user-plus"></i> Staff login
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Add clinic -->
    <admin-modal v-if="creating" size="lg" title="Add clinic" subtitle="Clinics are where doctors take appointments."
                 :busy="saving" @close="creating = false">
      <div v-if="formError" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ formError }}</div></div>
      <clinic-form-fields v-model="form" :cities="cities" :errors="formErrors" />
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="creating = false">Cancel</button>
        <button type="button" class="adm-btn adm-btn--primary" :disabled="saving" @click="createClinic">
          <span v-if="saving" class="adm-spinner"></span> Create clinic
        </button>
      </template>
    </admin-modal>

    <!-- Doctors at a clinic -->
    <admin-modal v-if="managing" :title="managing.clinicname" subtitle="Doctors who take appointments at this clinic."
                 :busy="saving" @close="managing = null">
      <div v-if="formError" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ formError }}</div></div>
      <div v-if="clinicDoctors === null">
        <div v-for="n in 2" :key="n" class="adm-skeleton" style="width: 60%; margin-bottom: 12px"></div>
      </div>
      <div v-else-if="!clinicDoctors.length" class="adm-empty adm-empty--tight">
        <i class="fas fa-user-md"></i>No doctors assigned yet.
      </div>
      <div v-else class="adm-options">
        <div v-for="doctor in clinicDoctors" :key="doctor.doctorid" class="adm-option is-disabled">
          <span class="adm-avatar adm-avatar--sm">{{ initials(doctor.doctorname) }}</span>
          <span class="adm-option__text">
            <span class="adm-option__title">{{ doctor.doctorname }}</span>
            <span class="adm-option__meta">{{ doctor.specialization || "—" }}</span>
          </span>
          <span class="adm-badge" :class="`adm-badge--${doctor.status || 'pending'}`">{{ statusLabel(doctor.status) }}</span>
        </div>
      </div>

      <h4 class="adm-section-title">Assign a doctor</h4>
      <div class="adm-assign-row">
        <select v-model="assignDoctorId" class="adm-select" aria-label="Doctor to assign" :disabled="!assignableDoctors.length">
          <option :value="null">{{ assignableDoctors.length ? "Select a doctor" : "No other doctors available" }}</option>
          <option v-for="doctor in assignableDoctors" :key="doctor.doctorid" :value="doctor.doctorid">
            {{ doctor.doctorname }} ({{ statusLabel(doctor.status) }})
          </option>
        </select>
        <button type="button" class="adm-btn adm-btn--primary" :disabled="!assignDoctorId || saving" @click="assignDoctor">
          <span v-if="saving" class="adm-spinner"></span> Assign
        </button>
      </div>
      <template #footer>
        <button type="button" class="adm-btn" @click="managing = null">Done</button>
      </template>
    </admin-modal>
  </div>
</template>

<script>
import axios from "axios";
import AdminModal from "./AdminModal.vue";
import ClinicFormFields, { clinicPayload, emptyClinic, validateClinic } from "./ClinicFormFields.vue";
import { errorMessage, initials, STATUS_LABELS } from "../admin-utils";

export default {
  name: "ClinicsPanel",
  // The shell passes every panel the same props; ignore the ones this panel does not use.
  inheritAttrs: false,
  components: { AdminModal, ClinicFormFields },
  props: {
    cities: { type: Array, default: () => [] },
    intent: { type: Object, default: null },
  },
  emits: ["notify", "changed", "navigate"],
  data() {
    return {
      clinics: [],
      loading: false,
      search: "",
      creating: false,
      form: emptyClinic(),
      formErrors: {},
      formError: "",
      saving: false,
      managing: null,
      clinicDoctors: null,
      allDoctors: [],
      assignDoctorId: null,
    };
  },
  computed: {
    filtered() {
      const term = this.search.toLowerCase();
      if (!term) return this.clinics;
      return this.clinics.filter(c =>
        [c.clinicname, c.clinicid, c.locationname].some(v => (v || "").toLowerCase().includes(term)));
    },
    assignableDoctors() {
      const linked = new Set((this.clinicDoctors || []).map(d => d.doctorid));
      return this.allDoctors.filter(d => !linked.has(d.doctorid) && d.status !== "rejected");
    },
  },
  methods: {
    initials,
    statusLabel(status) {
      return STATUS_LABELS[status] || "Pending";
    },
    isPlaceholderEmail(email) {
      return !email || email.endsWith("@yourdoc.local");
    },
    async fetchClinics() {
      this.loading = true;
      try {
        const { data } = await axios.get("/admin/clinics");
        this.clinics = data || [];
      } catch (error) {
        this.$emit("notify", errorMessage(error, "Could not load clinics."), "error");
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.form = emptyClinic();
      this.formErrors = {};
      this.formError = "";
      this.creating = true;
    },
    async createClinic() {
      this.formError = "";
      this.formErrors = validateClinic(this.form);
      if (Object.keys(this.formErrors).length) return;
      this.saving = true;
      try {
        await axios.post("/doctors/clinichospital", clinicPayload(this.form));
        this.$emit("notify", `${this.form.clinicname.trim()} was added.`);
        this.creating = false;
        this.$emit("changed");
        await this.fetchClinics();
      } catch (error) {
        this.formError = errorMessage(error, "Could not create the clinic.");
      } finally {
        this.saving = false;
      }
    },
    async openDoctors(clinic) {
      this.managing = clinic;
      this.clinicDoctors = null;
      this.assignDoctorId = null;
      this.formError = "";
      try {
        const requests = [axios.get(`/admin/clinics/${encodeURIComponent(clinic.clinicid)}/doctors`)];
        if (!this.allDoctors.length) {
          requests.push(axios.get("/doctors", { params: { status: "all", per_page: 100 } }));
        }
        const [linked, doctors] = await Promise.all(requests);
        this.clinicDoctors = linked.data || [];
        if (doctors) this.allDoctors = (doctors.data.doctors || []).slice().sort((a, b) => a.doctorname.localeCompare(b.doctorname));
      } catch (error) {
        this.clinicDoctors = this.clinicDoctors || [];
        this.formError = errorMessage(error, "Could not load doctors.");
      }
    },
    async assignDoctor() {
      this.saving = true;
      this.formError = "";
      try {
        const clinic = this.managing;
        await axios.post(`/doctors/${this.assignDoctorId}/clinics/${encodeURIComponent(clinic.clinicid)}/link`);
        const doctor = this.allDoctors.find(d => d.doctorid === this.assignDoctorId);
        this.$emit("notify", `${doctor ? doctor.doctorname : "Doctor"} now works at ${clinic.clinicname}.`);
        await Promise.all([this.openDoctors(clinic), this.fetchClinics()]);
      } catch (error) {
        this.formError = errorMessage(error, "Could not assign the doctor.");
      } finally {
        this.saving = false;
      }
    },
  },
  created() {
    this.fetchClinics();
    if (this.intent && this.intent.create) this.openCreate();
  },
};
</script>

<style scoped>
.adm-clinic-address {
  font-size: 12px;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.adm-assign-row {
  display: flex;
  gap: 10px;
}

.adm-empty--tight {
  padding: 20px;
}
</style>
